import { Link, useLocation } from 'react-router-dom'
import { useDepartment } from '../../context/DepartmentContext'
import { useTheme } from '../../context/ThemeContext'
import { useAuth } from '../../context/AuthContext'
import { ADMIN_EMAILS } from '../../config/adminConfig'
import {
  LayoutDashboard, Upload, CalendarDays, BarChart3, Flag, FileText, Settings,
  Home, Sun, Moon, LogOut, ShoppingCart, Package, Warehouse, Truck, Receipt,
  Calculator, Building2, AlertCircle, ChevronDown,
  BookOpen, Brain, Users, TrendingUp, Scale, Wallet, UserCheck
} from 'lucide-react'
import { useState, useRef, useEffect } from 'react'

const NAV_ITEMS = [
  { path: '/app/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/app/ingestion', label: 'Data Entry', icon: Upload },
  { path: '/app/reports', label: 'Reports', icon: FileText },
  { path: '/app/analytics', label: 'Insights', icon: TrendingUp },
  { path: '/app/dept-costing', label: 'Costing', icon: Calculator },
  { path: '/app/till', label: 'Till', icon: Wallet },
  { path: '/app/recruitment', label: 'Recruitment', icon: UserCheck, adminOnly: true },
  { path: '/app/admin', label: 'Admin', icon: Settings, adminOnly: true },
]

const POS_NAV_ITEMS = [
  { path: '/app/pos', label: 'POS', icon: ShoppingCart },
  { path: '/app/products', label: 'Products', icon: Package },
  { path: '/app/inventory', label: 'Inventory', icon: Warehouse },
  { path: '/app/suppliers', label: 'Suppliers', icon: Truck },
  { path: '/app/purchases', label: 'Purchases', icon: Receipt },
  { path: '/app/costing', label: 'Costing', icon: Calculator },
  { path: '/app/overhead', label: 'Overhead', icon: Building2 },
  { path: '/app/pos-reports', label: 'POS Reports', icon: FileText },
  { path: '/app/pos-flags', label: 'POS Flags', icon: AlertCircle },
  { path: '/app/accounting', label: 'Accounting', icon: BookOpen },
  { path: '/app/intelligence', label: 'Intelligence', icon: Brain },
  { path: '/app/staff-ops', label: 'Staff Ops', icon: Users },
]

export default function Navbar() {
  const location = useLocation()
  const { currentDepartment, clearDepartment } = useDepartment()
  const { theme, toggleTheme, isDark } = useTheme()
  const { user, userProfile, logout } = useAuth()
  const isAdmin = userProfile?.role === 'admin' || ADMIN_EMAILS.includes(user?.email)
  const visibleNavItems = NAV_ITEMS.filter(item => !item.adminOnly || isAdmin)
  const [posOpen, setPosOpen] = useState(false)
  const dropdownRef = useRef(null)

  const isPosActive = POS_NAV_ITEMS.some(item => location.pathname.startsWith(item.path))

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setPosOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <nav className="bg-gray-900 border-b border-gray-800 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-14">
          <div className="flex items-center gap-4">
            <Link to="/app/dashboard" className="text-[#E8761A] font-bold text-sm whitespace-nowrap">
              DESRES K&C
            </Link>
            <div className="hidden md:flex items-center gap-1">
              <button
                onClick={clearDepartment}
                className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors text-gray-400 hover:text-white hover:bg-gray-800/50"
                title="Back to Department Selection"
              >
                <Home size={14} />
                Home
              </button>
              {visibleNavItems.map(item => {
                const Icon = item.icon
                const isActive = location.pathname.startsWith(item.path)
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                      isActive
                        ? 'bg-gray-800 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                    }`}
                  >
                    <Icon size={14} />
                    {item.label}
                  </Link>
                )
              })}
              {/* POS Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setPosOpen(!posOpen)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                    isPosActive
                      ? 'bg-brand-700 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                  }`}
                >
                  <ShoppingCart size={14} />
                  Business
                  <ChevronDown size={12} className={`transition-transform ${posOpen ? 'rotate-180' : ''}`} />
                </button>
                {posOpen && (
                  <div className="absolute top-full left-0 mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-xl py-1 min-w-[180px] z-50">
                    {POS_NAV_ITEMS.map(item => {
                      const Icon = item.icon
                      const isActive = location.pathname.startsWith(item.path)
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setPosOpen(false)}
                          className={`flex items-center gap-2 px-4 py-2 text-xs transition-colors ${
                            isActive
                              ? 'bg-gray-800 text-white'
                              : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                          }`}
                        >
                          <Icon size={14} />
                          {item.label}
                        </Link>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {currentDepartment && (
              <span className="bg-brand-700 text-white text-xs px-2.5 py-1 rounded-full font-medium">
                {currentDepartment.displayName}
              </span>
            )}
            {userProfile && (
              <span className="text-xs text-gray-500 hidden md:block">
                {userProfile.displayName}
              </span>
            )}
            <button
              onClick={toggleTheme}
              className="text-gray-500 hover:text-white p-1.5 rounded transition-colors"
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button
              onClick={logout}
              className="text-gray-500 hover:text-red-400 p-1.5 rounded transition-colors"
              title="Sign Out"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        <div className="md:hidden flex overflow-x-auto gap-1 pb-2 -mx-1">
          <button
            onClick={clearDepartment}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs whitespace-nowrap text-gray-500"
          >
            <Home size={12} />
            Home
          </button>
          {visibleNavItems.map(item => {
            const Icon = item.icon
            const isActive = location.pathname.startsWith(item.path)
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded text-xs whitespace-nowrap ${
                  isActive ? 'bg-gray-800 text-white' : 'text-gray-500'
                }`}
              >
                <Icon size={12} />
                {item.label}
              </Link>
            )
          })}
          {POS_NAV_ITEMS.map(item => {
            const Icon = item.icon
            const isActive = location.pathname.startsWith(item.path)
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded text-xs whitespace-nowrap ${
                  isActive ? 'bg-brand-700 text-white' : 'text-gray-500'
                }`}
              >
                <Icon size={12} />
                {item.label}
              </Link>
            )
          })}
        </div>
      </div>
    </nav>
  )
}
